






/**
 * It allows to convert geographical coordinates into textual address representation using the TomTom's
 * [Search API - CrossStreet lookup](https://developer.tomtom.com/search-api/search-api-documentation-reverse-geocoding/crossstreet-lookup).
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "CrossStreetLookup/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The call is asynchronous therefore the user have two options for receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *    {{#crossLink "CrossStreetLookup/go:method"}}{{/crossLink}} method to handle the response.
 *
 * The final response from service is a JSON object with the following structure:
 *
 * ```js
 * {
 *   address: {
 *     buildingNumber: 19,
 *     streetNumber: 19,
 *     street: "N 2nd St",
 *     streetName: "N 2nd St",
 *     crossStreet: "E SANTA CLARA ST"
 *     streetNameAndNumber: "",
 *     speedLimit: "",
 *     countryCode: "US",
 *     countrySubdivision: "CA",
 *     countrySecondarySubdivision: "Santa Clara",
 *     countryTertiarySubdivision: "",
 *     municipality: "San Jose",
 *     postalCode: "95113",
 *     municipalitySubdivision: "San Francisco Bay Area",
 *     freeformAddress: "E SANTA CLARA ST &amp; N 2nd St, San Jose, CA, 95113"
 *   },
 *   position: "37.337083, -121.8898"
 * }
 * ```
 *
 * @example
 * ```js
 * tomtom.crossStreetLookup()
 *   .position('51.759,19.449')
 *   .go()
 *   .then(function(response) {
 *     console.log(response);
 *   });
 * ```
 *
 * @class CrossStreetLookup
 * @module Services
 * @uses Chain
 * @constructor
 * @uses RadiusMixin
 * @uses ProtocolMixin
 * @uses KeyMixin
 * @uses LimitMixin
 * @uses LanguageMixin
 * @uses PositionMixin
 * @uses SpatialKeysMixin
 * @uses HeadingMixin
 *
 * @param {Object} [options] Options to be passed to the cross street lookup call,
 *     or Array of such options objects to perform batch request.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 *
 * @param {String}   [options.key] An API Key issued from <a href="https://developer.tomtom.com">Developer Portal</a>
 * @param {"http"|"https"} [options.protocol="https"] Protocol's type
 *
 * @param {String|Array|Object} [options.position] Specific longitude and latitude of the location where the address
 *     is supposed to be found.
 *     The possible formats are explained in more detail in the
 *     {{#crossLink "CrossStreetLookup/position:method"}}{{/crossLink}} method.
 *     This option is required to make the call.
 *
 * @param {Number} [options.limit=10] Maximum number of responses that should be returned
 * @param {Boolean} [options.spatialKeys=false] If is enable the response will also contain a proprietary geo-spatial
 *     key information for a specified location
 * @param {Number} [options.heading] The directional heading of the vehicle in degrees, for travel along a
 *     segment of roadway.
 *     `0` is North, `90` is East and so on, values range from `-360` to `360`.
 *     The precision can include up to one decimal place.
 * @param {Number} [options.radius=10000] The maximum distance in meters from the specified position to consider
 * @param {String} [options.language] Language to use in the response.
 *     The list with all the available codes can be found
 *     <a href="https://developer.tomtom.com/search-api/search-api-documentation/supported-languages">here</a>.
 */

define(['./reverseGeocodeBase', './reverseGeocodeParameters', './types'],
    function(reverseGeocodeBase, reverseGeocodeParams, types) {
        'use strict';

        var ReverseGeocodeBasePrototype = reverseGeocodeBase.prototype.constructor;

        function CrossStreetLookup(options) {
            ReverseGeocodeBasePrototype.call(this, options);
            this.fields = reverseGeocodeParams(types.crossStreetLookup);
        }

        CrossStreetLookup.prototype = new ReverseGeocodeBasePrototype();
        CrossStreetLookup.prototype.constructor = CrossStreetLookup;

        return function(options) {
            return (new CrossStreetLookup(options)).construct();
        };
    });



// WEBPACK FOOTER //
// ./services/reverseGeocode/crossStreetLookup.js